import { createTheme } from '@mui/material/styles';

/**
 * FolioFlow Design System 2.0
 * 
 * Премиум коммерциялык SaaS палитрасы:
 * - Каркас: заманбап Slate/Obsidian тереңдиги.
 * - Акцент: интеллектуалдуу ИИ бирюзасы («Flow»).
 * - Шрифттер: Plus Jakarta Sans (баш сөздөр жана навигация) + Inter (интерфейс) + Lora (китеп тексти).
 */
export const PALETTES = {
  light: {
    bg: '#F8FAFC',
    surface: '#FFFFFF',
    surfaceSubtle: '#F1F5F9',
    accent: '#0D9488',
    accentDark: '#0F766E',
    gold: '#D97706',
    text: '#0F172A',
    subtitle: '#64748B',
    border: '#E2E8F0',
    soft: '#F0FDFA',
    success: '#059669',
    warning: '#D97706',
    danger: '#DC2626',
  },
  dark: {
    bg: '#0B0F17',
    surface: '#131D2E',
    surfaceSubtle: '#1C273B',
    accent: '#2DD4BF',
    accentDark: '#14B8A6',
    gold: '#FBBF24',
    text: '#F8FAFC',
    subtitle: '#94A3B8',
    border: '#223048',
    soft: 'rgba(45, 212, 191, 0.08)',
    success: '#34D399',
    warning: '#FBBF24',
    danger: '#F87171',
  },
};

/** `sx` ичинде колдонулуучу шилтемелер — маани `:root`тогу переменныйдан */
export const COLORS = Object.fromEntries(
  Object.keys(PALETTES.light).map((key) => [key, `var(--c-${kebab(key)})`]),
);

function kebab(s) {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

const toVars = (palette) => Object.fromEntries(
  Object.entries(palette).map(([key, value]) => [`--c-${kebab(key)}`, value]),
);

/** GlobalStyles үчүн: тема `<html data-theme>` атрибуту менен аныкталат */
export const THEME_VARS = {
  ':root': {
    ...toVars(PALETTES.light),
    '--font-editorial': '"Lora", "Merriweather", Georgia, serif',
    '--font-ui': '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  ':root[data-theme="dark"]': {
    ...toVars(PALETTES.dark),
    '--font-editorial': '"Lora", "Merriweather", Georgia, serif',
    '--font-ui': '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

/**
 * MUI темасы НАКТА hex маанилер менен курулат.
 * `palette`: 'light' | 'dark'; `textScale` — пайыз (терезелер үчүн)
 */
export function getTheme(palette = 'light', textScale = 100) {
  const p = PALETTES[palette] ?? PALETTES.light;
  const mode = palette === 'dark' ? 'dark' : 'light';

  return createTheme({
    palette: {
      mode,
      background: { default: p.bg, paper: p.surface },
      primary: { main: p.accent, dark: p.accentDark },
      secondary: { main: p.gold, contrastText: '#FFFFFF' },
      success: { main: p.success },
      warning: { main: p.warning },
      error: { main: p.danger },
      text: { primary: p.text, secondary: p.subtitle },
      divider: p.border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: { fontSize: 24, fontWeight: 800, letterSpacing: '-0.025em', color: p.text },
      h2: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: p.text },
      h3: { fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', color: p.text },
      h4: { fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', color: p.text },
      body1: { fontSize: 14, lineHeight: 1.6 },
      body2: { fontSize: 13, lineHeight: 1.5, color: p.subtitle },
      overline: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: p.subtitle },
      button: { fontWeight: 600, letterSpacing: '-0.01em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: mode === 'dark' ? `${p.border} transparent` : '#CBD5E1 transparent',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8, height: 8 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: mode === 'dark' ? p.border : '#CBD5E1',
              borderRadius: 4,
            },
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${p.border}`,
            borderRadius: 12,
            backgroundImage: 'none',
            backgroundColor: p.surface,
            boxShadow: mode === 'dark'
              ? '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px -1px rgba(0, 0, 0, 0.3)'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.03)',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          containedPrimary: {
            backgroundColor: p.accent,
            color: mode === 'dark' ? '#0B0F17' : '#FFFFFF',
            '&:hover': {
              backgroundColor: p.accentDark,
              boxShadow: mode === 'dark'
                ? `0 0 18px ${p.accent}50`
                : `0 4px 14px ${p.accent}35`,
            },
          },
          outlined: {
            borderColor: p.border,
            color: p.text,
            '&:hover': {
              borderColor: p.accent,
              backgroundColor: mode === 'dark' ? 'rgba(45, 212, 191, 0.08)' : 'rgba(13, 148, 136, 0.05)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${p.border}`,
            boxShadow: mode === 'dark'
              ? '0 24px 48px -12px rgba(0, 0, 0, 0.75)'
              : '0 24px 48px -12px rgba(15, 23, 42, 0.15)',
            zoom: textScale / 100,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            color: p.subtitle,
            fontSize: 11,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            backgroundColor: mode === 'dark' ? '#0F1726' : '#F1F5F9',
            borderBottom: `1px solid ${p.border}`,
            py: 1.5,
          },
          root: {
            borderBottom: `1px solid ${p.border}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          hover: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.03) !important' : 'rgba(0, 0, 0, 0.015) !important',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#FFFFFF',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: p.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: p.accent,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: p.accent,
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: 12,
            padding: '5px 10px',
            backgroundColor: mode === 'dark' ? '#1F2937' : '#0F172A',
            border: `1px solid ${p.border}`,
          },
        },
      },
    },
  });
}
